import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { Search } from 'lucide-react';
import { list_available_opportunities } from '../../../utils/manufacturing_services';
import './Produccion.css';

export function WonOpportunityPickerBody({
  token,
  busyId = null,
  onSelect,
  onInvalidSession,
  autoFocus = true,
}) {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const onInvalidRef = useRef(onInvalidSession);
  onInvalidRef.current = onInvalidSession;

  const load = useCallback(async () => {
    setLoading(true);
    const result = await list_available_opportunities(token);
    if (result === 'INVALID') {
      onInvalidRef.current?.();
      return;
    }
    if (!result || result.ok !== true) {
      setOpportunities([]);
    } else {
      setOpportunities(Array.isArray(result.opportunities) ? result.opportunities : []);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return opportunities;
    return opportunities.filter((opp) => {
      const hay = [opp.id, opp.name, opp.channel_name, opp.country]
        .map((v) => String(v ?? '').toLowerCase())
        .join(' ');
      return hay.includes(q);
    });
  }, [opportunities, search]);

  return (
    <>
      <div className="produccion-create-modal__search">
        <Search size={16} className="produccion-create-modal__search-icon" aria-hidden="true" />
        <Form.Control
          className="produccion-create-modal__search-input"
          placeholder="Buscar por nombre, canal o ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus={autoFocus}
        />
      </div>
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
      ) : filtered.length === 0 ? (
        <div className="produccion-create-modal__empty">No hay oportunidades disponibles.</div>
      ) : (
        <div className="produccion-opp-list">
          {filtered.map((opp) => (
            <button
              type="button"
              key={opp.id}
              className="produccion-opp-item"
              disabled={busyId != null}
              onClick={() => onSelect?.(opp)}
            >
              <div className="produccion-opp-item__main">
                <div className="produccion-opp-item__name">
                  {opp.name || `Oportunidad #${opp.id}`}
                </div>
                <div className="produccion-opp-item__meta">
                  #{opp.id}
                  {opp.channel_name ? ` · ${opp.channel_name}` : ''}
                  {opp.country ? ` · ${opp.country}` : ''}
                </div>
              </div>
              {busyId === opp.id ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Badge bg="success" className="produccion-opp-item__badge">WON</Badge>
              )}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

export default function WonOpportunityPickerModal({
  show,
  onHide,
  onSelect,
  busyId = null,
  token,
  onInvalidSession,
  title = 'Seleccionar oportunidad',
  hint = (
    <>
      Selecciona una oportunidad en estado <strong>WON</strong> que aún no tenga orden de manufactura.
    </>
  ),
}) {
  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      dialogClassName="produccion-create-modal"
      contentClassName="produccion-create-modal__content"
    >
      <Modal.Header closeButton className="produccion-create-modal__header">
        <Modal.Title className="produccion-create-modal__title">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="produccion-create-modal__body">
        <p className="produccion-create-modal__hint">{hint}</p>
        {show && (
          <WonOpportunityPickerBody
            token={token}
            busyId={busyId}
            onSelect={onSelect}
            onInvalidSession={onInvalidSession}
          />
        )}
      </Modal.Body>
      <Modal.Footer className="produccion-create-modal__footer">
        <Button variant="outline-secondary" onClick={onHide}>Cancelar</Button>
      </Modal.Footer>
    </Modal>
  );
}
